import { useState, useEffect } from "react";
import { Plus, X, GripVertical, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUpdateContractTemplate, ContractTemplate, ContractSection, MergeField } from "@/hooks/useContractTemplates";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ClauseLibrary, Clause } from "./ClauseLibrary";

interface EditContractTemplateDialogProps {
  template: ContractTemplate;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditContractTemplateDialog({ template, open, onOpenChange }: EditContractTemplateDialogProps) {
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description || "");
  const [category, setCategory] = useState(template.category);
  const [isDefault, setIsDefault] = useState(template.is_default);
  const [sections, setSections] = useState<ContractSection[]>(template.sections || []);
  const [mergeFields, setMergeFields] = useState<MergeField[]>(template.merge_fields || []);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const updateTemplate = useUpdateContractTemplate();

  useEffect(() => {
    setName(template.name);
    setDescription(template.description || "");
    setCategory(template.category);
    setIsDefault(template.is_default);
    setSections(template.sections || []);
    setMergeFields(template.merge_fields || []);
  }, [template]);

  const handleAddSection = () => {
    const newSection: ContractSection = {
      id: crypto.randomUUID(),
      name: "New Section",
      content: "",
      order: sections.length,
    };
    setSections([...sections, newSection]);
  };

  const handleUpdateSection = (id: string, field: keyof ContractSection, value: string | number) => {
    setSections(sections.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
  };

  const handleRemoveSection = (id: string) => {
    setSections(sections.filter((s) => s.id !== id));
  };

  const handleAddMergeField = () => {
    const newField: MergeField = {
      key: `field_${mergeFields.length + 1}`,
      label: "New Field",
      type: "text",
    };
    setMergeFields([...mergeFields, newField]);
  };

  const handleUpdateMergeField = (key: string, field: keyof MergeField, value: string) => {
    setMergeFields(
      mergeFields.map((f) =>
        f.key === key ? { ...f, [field]: value, key: field === "label" ? value.toLowerCase().replace(/\s+/g, "_") : f.key } : f
      )
    );
  };

  const handleRemoveMergeField = (key: string) => {
    setMergeFields(mergeFields.filter((f) => f.key !== key));
  };

  const handleInsertClause = (clause: Clause) => {
    if (activeSectionId) {
      setSections(sections.map((s) =>
        s.id === activeSectionId
          ? { ...s, content: s.content + (s.content ? "\n\n" : "") + clause.content }
          : s
      ));
    } else {
      const newSection: ContractSection = {
        id: crypto.randomUUID(),
        name: clause.name,
        content: clause.content,
        order: sections.length,
      };
      setSections([...sections, newSection]);
    }
  };

  const handleSubmit = () => {
    updateTemplate.mutate(
      {
        id: template.id,
        name,
        description: description || null,
        category,
        is_default: isDefault,
        sections,
        merge_fields: mergeFields,
      },
      {
        onSuccess: () => onOpenChange(false),
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Contract Template</DialogTitle>
          <DialogDescription>
            Update template sections and merge fields
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Template Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="service">Service Agreement</SelectItem>
                  <SelectItem value="software">Software License</SelectItem>
                  <SelectItem value="consulting">Consulting</SelectItem>
                  <SelectItem value="nda">NDA</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="is-default"
              checked={isDefault}
              onCheckedChange={setIsDefault}
            />
            <Label htmlFor="is-default">Set as default template</Label>
          </div>

          <Tabs defaultValue="sections" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="sections">Sections ({sections.length})</TabsTrigger>
              <TabsTrigger value="fields">Merge Fields ({mergeFields.length})</TabsTrigger>
              <TabsTrigger value="clauses">
                <BookOpen className="h-4 w-4 mr-1" />
                Clauses
              </TabsTrigger>
            </TabsList>

            <TabsContent value="sections" className="space-y-3 mt-4">
              <p className="text-sm text-muted-foreground">
                Click a section to select it, then insert clauses from the Clauses tab.
              </p>
              {sections.map((section) => (
                <Card 
                  key={section.id}
                  className={`cursor-pointer transition-all ${activeSectionId === section.id ? 'ring-2 ring-primary' : ''}`}
                  onClick={() => setActiveSectionId(section.id)}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <GripVertical className="h-5 w-5 text-muted-foreground mt-2 cursor-move" />
                      <div className="flex-1 space-y-3">
                        <Input
                          value={section.name}
                          onChange={(e) => handleUpdateSection(section.id, "name", e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Textarea
                          value={section.content}
                          onChange={(e) => handleUpdateSection(section.id, "content", e.target.value)}
                          rows={3}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveSection(section.id);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              <Button variant="outline" onClick={handleAddSection} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Section
              </Button>
            </TabsContent>

            <TabsContent value="clauses" className="mt-4">
              <ClauseLibrary mode="select" onInsertClause={handleInsertClause} />
              {activeSectionId && (
                <p className="text-sm text-primary mt-2">
                  ✓ Inserting into: {sections.find(s => s.id === activeSectionId)?.name}
                </p>
              )}
            </TabsContent>

            <TabsContent value="fields" className="space-y-3 mt-4">
              <p className="text-sm text-muted-foreground mb-3">
                Use merge fields in sections with {"{{field_key}}"} syntax
              </p>
              {mergeFields.map((field) => (
                <div key={field.key} className="flex items-center gap-3">
                  <Input
                    value={field.label}
                    onChange={(e) => handleUpdateMergeField(field.key, "label", e.target.value)}
                    className="flex-1"
                  />
                  <code className="text-xs bg-muted px-2 py-1 rounded min-w-[120px]">
                    {`{{${field.key}}}`}
                  </code>
                  <Select
                    value={field.type}
                    onValueChange={(v) => handleUpdateMergeField(field.key, "type", v)}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">Text</SelectItem>
                      <SelectItem value="number">Number</SelectItem>
                      <SelectItem value="date">Date</SelectItem>
                      <SelectItem value="currency">Currency</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveMergeField(field.key)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" onClick={handleAddMergeField} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Merge Field
              </Button>
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!name || updateTemplate.isPending}>
            {updateTemplate.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
